import { FunctionComponent, useMemo, useState } from 'react'
import { Input } from '@pubstack/ui/components/Input'
import { CsvParserErrorType } from '@pubstack/common/src/csv-parser-error'
import { CsvParser } from '@pubstack/common/src/csv-parser'
import { ErrorListDisplay } from '@pubstack/ui/components/ErrorList'
import { Icon } from '@pubstack/ui/components/Icon'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { Alert } from '@pubstack/ui/components/Alert'
import { Colors } from '~/assets/style/colors'
import { FlyoutActions, FlyoutBody, FlyoutTitle, Section } from '@pubstack/ui/components/GlobalFlyoutWrapper'
import { TrackingLabel } from '@pubstack/ui/types/tracking'
import { ChipSelectableOptionGroupProp, ChipSelectableOptionProp } from '@pubstack/ui/components/ChipListInput'
import { Button } from '@pubstack/ui/components/Button'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { ChipListInput } from '@pubstack/ui/components/ChipListInput'
import { MultiSelectInput } from '@pubstack/ui/components/MultiSelectInput'
import { AdQualityRule, CreateAdQualityRule } from '@pubstack/common/src/ad-quality-rule'
import { IAB_CATEGORY_MAP } from '@pubstack/common/src/pbs/pbs-ad-quality-config'
import { useAdQualityRules, useCreateAdQualityRule, useUpdateAdQualityRule } from '~/api/ad-quality.api'
import { Dropzone } from '@pubstack/ui/components/Dropzone'
import { useSitesByIds } from '~/api/app/sites.api'
import { Site } from '@pubstack/common/src/site'
import { useMediaSearch } from './useMediaSearch'
import { Toggle } from '~/components/Toggle'
import { formatDate } from '@pubstack/ui/utils/dates'
import { downloadCSVData } from '@pubstack/ui/utils/fileDownload'

export interface AdQualityForm {
  ruleName?: string
  siteIds?: string[]
  blockedCategories?: string[]
}

type Props = {
  onClose: () => void
  rule?: AdQualityRule
  trackingLabel: TrackingLabel
  sites: Site[]
  scopeRuleId?: string
}

const sectionTitleClassName = 'text-jet font-bold'

const getMainCategoryCode = (code: string) => code.split('-')[0]

const IAB_CODES_BY_MAIN_CATEGORY = Object.keys(IAB_CATEGORY_MAP).reduce<Record<string, string[]>>((acc, code) => {
  const mainCode = getMainCategoryCode(code)
  acc[mainCode] = [...(acc[mainCode] ?? []), code]
  return acc
}, {})

export const expandBlockedCategories = (selection: string[] = []): string[] => [...new Set(selection.flatMap((code) => IAB_CODES_BY_MAIN_CATEGORY[code] ?? [code]))]

export const collapseBlockedCategories = (categories: string[] = []): string[] => {
  const selected = new Set(categories)
  return [...new Set(categories.filter((code) => code === getMainCategoryCode(code) || !selected.has(getMainCategoryCode(code))))]
}

export const EditAdQualityFlyout: FunctionComponent<Props> = ({ onClose, rule, trackingLabel, scopeRuleId }) => {
  const [blockingDomainsFile, setBlockingDomainsFile] = useState<File | null>()
  const [blockedDomains, setBlockedDomains] = useState<string[]>(rule?.blockedAdvertiserDomains ?? [])
  const [csvErrors, setCsvErrors] = useState<CsvParserErrorType[]>([])
  const isToggleLocked = !!scopeRuleId && rule?.id !== scopeRuleId

  const [isRuleBySite, setIsRuleBySite] = useState<boolean>(isToggleLocked || rule?.blocklistType === 'site')
  const user = useAuthenticatedUser()

  const [mediaSearch, setMediaSearch] = useState('')
  const { sites, knownSites, isLoading: isLoadingSites, isSearching: isSearchingSites, isLoadingMore, loadMore } = useMediaSearch(user.currentScopeId, mediaSearch)

  const saveErrorMessage = 'Rule could not be saved. Please try again. Contact the support if the issue persists.'

  const { data: existingRules } = useAdQualityRules(user.currentScopeId)

  const siteToRuleName = useMemo(() => {
    const map = new Map<string, string>()
    for (const existingRule of existingRules) {
      if (existingRule.id === rule?.id || !existingRule.siteIds) continue
      for (const siteId of existingRule.siteIds) {
        map.set(siteId, existingRule.name)
      }
    }
    return map
  }, [existingRules, rule?.id])

  const downloadRule = (rule: AdQualityRule) => {
    let csvContent = 'adomain\n'
    rule.blockedAdvertiserDomains?.forEach((domain) => {
      csvContent += `${domain}\n`
    })
    downloadCSVData(csvContent, `${rule.name}-ad-quality-domains-backup`)
  }

  const sitesOptions = useMemo<ChipSelectableOptionProp<string>[]>(
    () =>
      sites.map((site) => {
        const usedByRule = siteToRuleName.get(site.id)
        return {
          value: site.id,
          label: usedByRule ? `${site.name} (${usedByRule})` : site.name,
          trackingLabel: `${trackingLabel}-site-set`,
          disabled: !!usedByRule,
        }
      }),
    [sites, trackingLabel, siteToRuleName]
  )

  const validateRuleName = (ruleName?: string | string[]) => {
    const name = (typeof ruleName === 'string' ? ruleName : '').trim()
    if (!name) {
      return 'Rule name is required'
    }
    const isNameTaken = existingRules.some((existingRule: AdQualityRule) => existingRule.id !== rule?.id && existingRule.name.trim().toLowerCase() === name.toLowerCase())
    return !isNameTaken || 'A rule with this name already exists'
  }

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors: formErrors, isValid, isDirty },
  } = useForm<AdQualityForm>({
    mode: 'onChange',
    defaultValues: {
      ruleName: rule?.name ?? '',
      siteIds: rule?.siteIds,
      blockedCategories: collapseBlockedCategories(rule?.blockedCategories),
    },
  })
  const {
    mutateAsync: createRule,
    isPending: isCreating,
    error: createError,
  } = useCreateAdQualityRule(user.currentScopeId, {
    successMessage: (
      <>
        <strong>{rule?.name ?? getValues('ruleName')}</strong> successfully created.
      </>
    ),
    errorMessage: saveErrorMessage,
  })
  const {
    mutateAsync: updateRule,
    isPending: isUpdating,
    error: updateError,
  } = useUpdateAdQualityRule(rule?.id ?? '', user.currentScopeId, {
    successMessage: (
      <>
        <strong>{rule?.name}</strong> successfully updated.
      </>
    ),
    errorMessage: saveErrorMessage,
  })

  const isSaving = isCreating || isUpdating
  const blockedCategories = useWatch({ control, name: 'blockedCategories' })
  const selectedSiteIds = useWatch({ control, name: 'siteIds' })

  const missingSiteIds = useMemo(() => (isLoadingSites ? [] : (selectedSiteIds ?? []).filter((siteId) => !knownSites[siteId])), [isLoadingSites, selectedSiteIds, knownSites])
  const { data: missingSites, isLoading: isLoadingMissingSites } = useSitesByIds(missingSiteIds, user.currentScopeId)
  // The chips wait for every name at once rather than showing ids that flip to names one lookup at a time
  const isResolvingSiteNames = isLoadingSites || isLoadingMissingSites
  const siteNamesById = useMemo(() => Object.fromEntries([...Object.values(knownSites), ...missingSites].map((site: Pick<Site, 'id' | 'name'>) => [site.id, site.name])), [knownSites, missingSites])
  const hasBlockingCriteria = (blockedCategories ?? []).length > 0 || blockedDomains.length > 0
  const haveDomainsChanged = useMemo(() => {
    const savedDomains = new Set(rule?.blockedAdvertiserDomains ?? [])
    return blockedDomains.length !== savedDomains.size || blockedDomains.some((domain) => !savedDomains.has(domain))
  }, [rule, blockedDomains])
  const hasChanges = isDirty || haveDomainsChanged

  const handleUpload = async (file: File | null) => {
    setBlockingDomainsFile(file)
    setCsvErrors([])
    if (!file) {
      setBlockedDomains([])
      return
    }

    const content = await file.text()
    const parsed = CsvParser.parse<{ adomain: string }>(content, ['adomain'], { forbiddenChars: /[\s'"\u2018\u2019,;:<>]/ })

    if (!parsed.success) {
      setCsvErrors(parsed.errors)
      setBlockedDomains([])
      return
    }

    setBlockedDomains(parsed.data.map((row) => row.adomain).filter(Boolean))
  }

  const mutationError = (createError ?? updateError) as { type?: string; error?: unknown } | null
  const allErrors: CsvParserErrorType[] = [...csvErrors, ...(mutationError?.type === 'CsvParserError' ? (mutationError.error as CsvParserErrorType[]) : [])]

  const errorDisplay =
    !blockingDomainsFile || allErrors.length === 0 ? null : (
      <Alert variant="danger" iconName={'info'}>
        <ErrorListDisplay errors={allErrors} />
      </Alert>
    )

  const blockedCategoriesOptions = useMemo<ChipSelectableOptionGroupProp[]>(() => {
    const selectedMainCategories = new Set((blockedCategories ?? []).filter((code) => code === getMainCategoryCode(code)))
    const groups = new Map<string, ChipSelectableOptionGroupProp>()
    for (const [code, label] of Object.entries(IAB_CATEGORY_MAP)) {
      const parentCode = getMainCategoryCode(code)
      const group = groups.get(parentCode) ?? { label: parentCode, options: [] }
      if (code === parentCode) {
        // the top level category stays selectable, as a first row blocking the whole group
        group.label = label
        group.options.unshift({ value: code, label: `${code} - All ${label.slice(label.indexOf(' - ') + 3)}`, trackingLabel: `${trackingLabel}-category-set` })
      } else {
        group.options.push({ value: code, label, trackingLabel: `${trackingLabel}-category-set`, disabled: selectedMainCategories.has(parentCode) })
      }
      groups.set(parentCode, group)
    }
    return [...groups.values()]
  }, [trackingLabel, blockedCategories])

  const onAdQualitySubmit: SubmitHandler<AdQualityForm> = async (form) => {
    const toSave: CreateAdQualityRule = {
      name: (form.ruleName ?? '').trim(),
      siteIds: isRuleBySite ? form.siteIds : undefined,
      blockedCategories: expandBlockedCategories(form.blockedCategories),
      blockedAdvertiserDomains: blockedDomains,
    }

    try {
      await (rule ? updateRule(toSave) : createRule(toSave))
      onClose()
    } catch {
      // The mutation already raised an error toast: keep the flyout open so the user can retry
    }
  }

  return (
    <>
      <FlyoutTitle>{rule ? 'Edit rule' : 'New rule'}</FlyoutTitle>
      <FlyoutBody>
        <Section className="m-1">
          <p className={sectionTitleClassName}>Identification</p>
          <form>
            <Input className="w-full" name="ruleName" type="text" labelIsPlaceholder label="Rule name" control={control} rules={{ validate: validateRuleName }} error={formErrors.ruleName?.message} />
          </form>
        </Section>
        <Section className="m-1">
          <p className={sectionTitleClassName}>Setting</p>
          <span>Set up at scope level or select one or multiple media where you want to block categories or domains. A media can only be selected on one rule.</span>
          <div className="mb-3 mt-3">
            <Toggle
              className="mr-1.25"
              value={isRuleBySite}
              onClick={() => {
                if (isToggleLocked) return
                if (isRuleBySite) {
                  setValue('siteIds', [], { shouldDirty: true })
                }
                setIsRuleBySite(!isRuleBySite)
              }}
              disabled={isToggleLocked}
              trackingLabel={`${trackingLabel}-rule-by-site-set`}
            />
            <span>Apply ad quality settings for specific medias</span>
          </div>
          <div className="w-full">
            <MultiSelectInput
              name="siteIds"
              control={control}
              rules={{ validate: (siteIds) => !isRuleBySite || (siteIds ?? []).length > 0 || 'Select at least one media' }}
              error={formErrors.siteIds?.message}
              selectableOptions={sitesOptions}
              onSearchChange={setMediaSearch}
              isSearching={isSearchingSites || isLoadingSites}
              labelsByValue={siteNamesById}
              isLoadingLabels={isResolvingSiteNames}
              onReachEnd={loadMore}
              isLoadingMore={isLoadingMore}
              chipColor={Colors.Topaze}
              label="Select media"
              labelIsPlaceholder
              entityLabel="media"
              displayLabel
              trackingLabel={`${trackingLabel}-site-set`}
              disabled={!isRuleBySite}
            />
          </div>
        </Section>
        <Section className="m-1">
          <p className={sectionTitleClassName}>IAB categories</p>
          <div className="w-full">
            <ChipListInput
              name="blockedCategories"
              control={control}
              selectableOptions={blockedCategoriesOptions}
              onChange={(values) => setValue('blockedCategories', collapseBlockedCategories(values), { shouldDirty: true })}
              chipColor={Colors.Topaze}
              label="Selected categories"
              isPasteAllowed={false}
              displayLabel
            />
          </div>
        </Section>
        {rule && (
          <Section>
            <p className={sectionTitleClassName}>Backup file</p>
            <p className="mt-1 mb-3">Get a backup of your latest upload. Please keep a local copy of your most recent file.</p>
            <div className="flex gap-2 [&>span]:flex [&>span]:items-center [&>span]:text-sm [&>span]:text-gray-400">
              <Button variant="primary" iconName={'download'} iconSize={'md'} trackingLabel={`${trackingLabel}-download`} disabled={!blockedDomains?.length} onClick={() => downloadRule(rule)}>
                Download backup
              </Button>
              {blockedDomains?.length > 0 && <span className="color-hurricane">last backup on {formatDate(rule.updatedAt)}</span>}
            </div>
          </Section>
        )}
        <Section className="m-1">
          <p className={sectionTitleClassName}>Domain blocklist</p>
          <p className="pb-2!">Transfer your URL domains files into Pubstack.</p>
          <Dropzone onDrop={handleUpload} currentFile={blockingDomainsFile} loading={blockingDomainsFile ? isSaving : false} />
          <span className="mt-1">{errorDisplay}</span>
        </Section>
        {!hasBlockingCriteria && (
          <span className="text-sm flex items-center gap-1 mt-0.5 ml-0.5" style={{ color: Colors.Alert }}>
            <Icon name="alert" size="sm" color={Colors.Alert} />
            Choose an IAB category or attach a domain blocklist file to create this rule.
          </span>
        )}
      </FlyoutBody>
      <FlyoutActions>
        <Button onClick={handleSubmit(onAdQualitySubmit)} disabled={isSaving || !isValid || !hasBlockingCriteria || !hasChanges} trackingLabel={`${trackingLabel}-apply`}>
          {isSaving ? 'Applying...' : 'Apply'}
        </Button>
      </FlyoutActions>
    </>
  )
}
